import React, { useEffect, useState, useCallback } from "react";
import DashboardContainer from "../../components/dashboard-container";
import {
  Box,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Button,
  Snackbar,
  Alert,
} from "@mui/material";
import { kafkaDeadLetterService } from "../../utils/services/kafka-dead-letter.service";
import KafkaDeadLetterList from "../../components/kafka-dead-letter-list";

const STATUSES = ["pending", "reprocessing", "resolved"];

const KafkaDeadLetter = () => {
  const [data, setData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(15);
  const [dataCount, setDataCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [topicFilter, setTopicFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("pending");
  const [availableTopics, setAvailableTopics] = useState([]);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const filters = {};
      if (topicFilter) filters.topic = topicFilter;
      if (statusFilter) filters.status = statusFilter;
      const response = await kafkaDeadLetterService.fetch(
        rowsPerPage,
        page * rowsPerPage,
        filters,
      );
      setData(response.data || []);
      setDataCount(response.total || 0);
    } catch (error) {
      console.error("Fetch failed:", error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, topicFilter, statusFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    kafkaDeadLetterService
      .fetchTopics()
      .then(setAvailableTopics)
      .catch(() => {});
  }, []);

  const handleRetrigger = async (id) => {
    setLoadingId(id);
    try {
      await kafkaDeadLetterService.retrigger(id);
      setSnackbar({
        open: true,
        message: "Message retriggered — republished to Kafka topic.",
        severity: "success",
      });
      await fetchData();
    } catch (e) {
      setSnackbar({
        open: true,
        message: `Retrigger failed: ${e?.response?.data?.message || e.message}`,
        severity: "error",
      });
    } finally {
      setLoadingId(null);
    }
  };

  const handleResolve = async (id) => {
    setLoadingId(id);
    try {
      await kafkaDeadLetterService.resolve(id);
      setSnackbar({
        open: true,
        message: "Marked as resolved.",
        severity: "success",
      });
      await fetchData();
    } catch (e) {
      setSnackbar({
        open: true,
        message: `Failed: ${e?.response?.data?.message || e.message}`,
        severity: "error",
      });
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <>
      <DashboardContainer
        filterButton={false}
        hideUploadButton
        header={"Kafka Dead Letters"}
        hideAddButton={true}
        hideFilterClient={true}
        onClickRefresh={fetchData}
      >
        {/* Filters */}
        <Stack
          direction="row"
          spacing={2}
          sx={{ mb: 2, flexWrap: "wrap", alignItems: "center" }}
        >
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Topic</InputLabel>
            <Select
              value={topicFilter}
              label="Topic"
              onChange={(e) => {
                setTopicFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All topics</MenuItem>
              {availableTopics.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              setTopicFilter("");
              setStatusFilter("pending");
              setPage(0);
            }}
          >
            Reset Filters
          </Button>
        </Stack>

        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              zIndex: 9999,
              color: "white",
            }}
          >
            <CircularProgress color="inherit" />
          </Box>
        ) : (
          <KafkaDeadLetterList
            data={data}
            dataCount={dataCount}
            currentPage={page}
            itemsPerPage={rowsPerPage}
            setPage={setPage}
            setRowsPerPage={setRowsPerPage}
            onRetrigger={handleRetrigger}
            onResolve={handleResolve}
            loadingId={loadingId}
          />
        )}
      </DashboardContainer>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default KafkaDeadLetter;
